import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import PageHeader from '../components/PageHeader';
import TurnstileWidget from '../components/TurnstileWidget';

const Container = styled.div`
  padding: 2rem;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Description = styled.div`
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 15px;
  text-align: center;
  margin-bottom: 2rem;

  p {
    font-size: 1.1rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
  }
`;

const StyledForm = styled(Form)`
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 15px;
  margin-bottom: 2rem;
  border: 2px solid rgba(255, 51, 51, 0.3);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: rgba(255, 255, 255, 0.9);
`;

const Input = styled(Field)`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid rgba(255, 51, 51, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgba(255, 51, 51, 0.6);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const TextArea = styled(Field)`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid rgba(255, 51, 51, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  min-height: 140px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgba(255, 51, 51, 0.6);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const ErrorMessage = styled.div`
  color: #ff3333;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const Button = styled.button`
  background: rgba(255, 51, 51, 0.8);
  color: white;
  border: none;
  padding: 0.8rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    background: rgba(255, 51, 51, 1);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 51, 51, 0.3);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
`;

const TurnstileContainer = styled.div`
  margin: 1.5rem 0;
  display: flex;
  justify-content: center;
`;

const CheckboxLabel = styled.label`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
`;

const CheckboxBox = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid rgba(255, 51, 51, 0.6);
  background: rgba(0, 0, 0, 0.35);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  flex: 0 0 18px;
  transition: all 0.2s ease;

  &::after {
    content: '';
    display: block;
    width: 6px;
    height: 10px;
    border-right: 3px solid rgba(255, 255, 255, 0.95);
    border-bottom: 3px solid rgba(255, 255, 255, 0.95);
    box-sizing: border-box;
    opacity: 0;
    transform: rotate(45deg) scale(0.9);
    transition: all 0.2s ease;
  }

  &[data-checked='true'] {
    border-color: rgba(255, 51, 51, 0.9);
    background: rgba(255, 51, 51, 0.15);
  }

  &[data-checked='true']::after {
    opacity: 1;
    transform: rotate(45deg) scale(1);
  }
`;

const CheckboxInput = styled.input`
  position: absolute;
  inset: 0;
  width: 18px;
  height: 18px;
  opacity: 0;
  margin: 0;
  cursor: pointer;

  &:focus-visible + ${CheckboxBox} {
    outline: 2px solid rgba(255, 51, 51, 0.7);
    outline-offset: 3px;
  }
`;

const validationSchema = Yup.object({
  account_name: Yup.string().required('Account name is required').min(2).max(200),
  requester_name: Yup.string().required('Requester name is required').min(2).max(100),
  owner_email: Yup.string().required('Owner email is required').email('Invalid email').max(254),
  new_owner_name: Yup.string().required('New owner name is required').min(2).max(100),
  new_owner_email: Yup.string().required('New owner email is required').email('Invalid email').max(254),
  folder_path_or_share_link: Yup.string().required('Folder path or share link is required').min(2).max(1000),
  confirmation_checkbox: Yup.bool().oneOf([true], 'Confirmation is required')
});

function OwnershipTransfer() {
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
  const turnstileRef = React.useRef(null);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const token = turnstileToken;
      if (!token) {
        setSubmitError('Please complete the Cloudflare Turnstile verification');
        setSubmitting(false);
        return;
      }

      const res = await fetch('/api/ownership-transfer', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          ...values,
          turnstileToken: token
        })
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.ok) {
        setSubmitError(data?.error || 'Failed to submit request. Please try again.');
        setTurnstileToken(null);
        turnstileRef.current?.reset?.();
        setSubmitting(false);
        return;
      }

      setShowSuccess(true);
      setSubmitError('');
      resetForm();
      setTurnstileToken(null);
      turnstileRef.current?.reset?.();
      setTimeout(() => setShowSuccess(false), 6000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError('Failed to submit request. Please try again.');
    }

    setSubmitting(false);
  };

  return (
    <Container>
      <PageHeader title="Ownership Transfer Request" highlightWord="Transfer" />
      <Description>
        <p>
          Use this form to request a folder ownership transfer. Our team will review the request and follow up.
        </p>
      </Description>

      <Formik
        initialValues={{
          account_name: '',
          requester_name: '',
          owner_email: '',
          new_owner_name: '',
          new_owner_email: '',
          folder_path_or_share_link: '',
          message: '',
          confirmation_checkbox: false
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, isSubmitting }) => (
          <StyledForm>
            {showSuccess && (
              <SuccessMessage>
                Request submitted successfully. Our team will contact you soon.
              </SuccessMessage>
            )}

            {submitError && (
              <ErrorMessage style={{ marginBottom: '1rem', textAlign: 'center' }}>
                {submitError}
              </ErrorMessage>
            )}

            <FormGroup>
              <Label htmlFor="account_name">
                Account Name
              </Label>
              <Input id="account_name" name="account_name" placeholder="Your RedCloud Account Name" />
              {errors.account_name && touched.account_name && (
                <ErrorMessage>{errors.account_name}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="requester_name">Your Name</Label>
              <Input id="requester_name" name="requester_name" placeholder="Enter your full name" />
              {errors.requester_name && touched.requester_name && (
                <ErrorMessage>{errors.requester_name}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="owner_email">Current Owner Email</Label>
              <Input id="owner_email" name="owner_email" type="email" placeholder="owner@example.com" />
              {errors.owner_email && touched.owner_email && (
                <ErrorMessage>{errors.owner_email}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="new_owner_name">New Owner Name</Label>
              <Input id="new_owner_name" name="new_owner_name" placeholder="Enter the new owner's full name" />
              {errors.new_owner_name && touched.new_owner_name && (
                <ErrorMessage>{errors.new_owner_name}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="new_owner_email">New Owner Email</Label>
              <Input id="new_owner_email" name="new_owner_email" type="email" placeholder="new-owner@example.com" />
              {errors.new_owner_email && touched.new_owner_email && (
                <ErrorMessage>{errors.new_owner_email}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="folder_path_or_share_link">Folder Path or Share Link</Label>
              <TextArea
                component="textarea"
                id="folder_path_or_share_link"
                name="folder_path_or_share_link"
                placeholder="Paste the folder path or a share link..."
              />
              {errors.folder_path_or_share_link && touched.folder_path_or_share_link && (
                <ErrorMessage>{errors.folder_path_or_share_link}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="message">Notes (Optional)</Label>
              <TextArea component="textarea" id="message" name="message" placeholder="Any extra context for our team..." />
            </FormGroup>

            <FormGroup>
              <Field name="confirmation_checkbox">
                {({ field, form }) => (
                  <CheckboxLabel>
                    <span style={{ position: 'relative', display: 'inline-flex' }}>
                      <CheckboxInput
                        type="checkbox"
                        name={field.name}
                        checked={Boolean(field.value)}
                        onChange={(e) => form.setFieldValue(field.name, e.target.checked)}
                        onBlur={field.onBlur}
                      />
                      <CheckboxBox data-checked={Boolean(field.value)} />
                    </span>
                    <span>I confirm I am authorized to request this change.</span>
                  </CheckboxLabel>
                )}
              </Field>
              {errors.confirmation_checkbox && touched.confirmation_checkbox && (
                <ErrorMessage>{errors.confirmation_checkbox}</ErrorMessage>
              )}
            </FormGroup>

            <TurnstileContainer>
              <TurnstileWidget
                ref={turnstileRef}
                siteKey={turnstileKey}
                theme="dark"
                onToken={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
              />
            </TurnstileContainer>

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </StyledForm>
        )}
      </Formik>
    </Container>
  );
}

export default OwnershipTransfer;
